import { ExternalLink, FileText } from "lucide-react";
import type { ModalComponentProps } from "@/lib/modal";
import {
  ResponsiveModalDescription,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { Badge } from "@/components/ui/badge";
import type { Document } from "@/lib/api/knowledge-base";
import {
  getSourceIcon,
  getSourceLabel,
} from "@/components/knowledge-base/document-utils";
import { formatDate } from "@/lib/format";

export interface ViewDocumentModalProps {
  document: Document;
}

export function ViewDocumentModal({
  document,
}: ViewDocumentModalProps & ModalComponentProps) {
  return (
    <div className="space-y-4">
      <ResponsiveModalHeader>
        <div className="flex items-center gap-3 text-left">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500/10 to-red-500/10">
            <FileText className="size-5 text-rose-500" />
          </div>
          <div className="min-w-0 flex-1">
            <ResponsiveModalTitle className="truncate">
              {document.title || `Document #${document.id}`}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription asChild className="mt-1">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="gap-1">
                  {getSourceIcon(document.sourceType)}
                  {getSourceLabel(document.sourceType)}
                </Badge>
                <span className="text-muted-foreground text-sm">
                  {formatDate(document.createdAt)}
                </span>
                {document.sourceUrl && (
                  <a
                    href={document.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-rose-500 transition-colors hover:text-rose-400"
                  >
                    <ExternalLink className="size-3.5" />
                    Source
                  </a>
                )}
              </div>
            </ResponsiveModalDescription>
          </div>
        </div>
      </ResponsiveModalHeader>

      <div className="bg-muted/30 max-h-[50vh] overflow-y-auto rounded-lg border p-4">
        <pre className="text-foreground/80 whitespace-pre-wrap font-mono text-sm leading-relaxed">
          {document.content}
        </pre>
      </div>
    </div>
  );
}

ViewDocumentModal.contentClassName = "max-w-2xl";
ViewDocumentModal.displayName = "ViewDocumentModal";
